import { Locales } from "adapters/i18n";
import { DATE_FORMATS, DATE_UNIT, DateUtils } from "shared/dateUtils";
import type {
  EnrichedAnimalCategory,
  LivestockInfoFromFarm,
  ProjectedWeight,
  WeightGainCurve,
} from "shared/types";
import type { LotsFilterFormState } from "../lots.types";

export const MAGNIFYING_GLASS_IMAGE_URL = "../images/busca.png";
export const RETREAT_FILTER_ALL_VALUE = -1;

export enum MeasuredWeightFilter {
  All,
  Informed,
  NotInformed,
}

export enum ProjectedWeightFilter {
  All,
  Available,
  NotAvailable,
}

export enum LotListTableColumn {
  Lot = "lot",
  Quantity = "quantity",
  Sanity = "sanity",
  Goal = "goal",
  GoalWeight = "goal-weight",
  MeasuredWeight = "measured-weight",
  WeighingDate = "weighing-date",
  ProjectedWeight = "projected-weight",
}

export enum LotSanity {
  None = "none",
  Good = "good",
  Bad = "bad",
}

export function parseWeightInput(value: string): number | null {
  const parsed = value.trim() === "" ? null : Number(value);
  const weight = parsed != null && !isNaN(parsed) ? parsed : null;

  return weight;
}

function getSanityValue(row: LivestockInfoFromFarm): LotSanity {
  if (!row.healthCheck) {
    return LotSanity.None;
  }

  if (
    row.healthCheck.animalsWithParasites > 0 ||
    row.healthCheck.otherHealthProblems > 0 ||
    !row.healthCheck.parasitesFree
  ) {
    return LotSanity.Bad;
  }

  return LotSanity.Good;
}

export function getValueByColumn(
  row: LivestockInfoFromFarm,
  column: LotListTableColumn.Lot,
): string | null;
export function getValueByColumn(
  row: LivestockInfoFromFarm,
  column: LotListTableColumn.Quantity,
): number;
export function getValueByColumn(
  row: LivestockInfoFromFarm,
  column: LotListTableColumn.Sanity,
): LotSanity;
export function getValueByColumn(
  row: LivestockInfoFromFarm,
  column: LotListTableColumn.Goal,
): number | null;
export function getValueByColumn(
  row: LivestockInfoFromFarm,
  column: LotListTableColumn.GoalWeight,
): number | null;
export function getValueByColumn(
  row: LivestockInfoFromFarm,
  column: LotListTableColumn.MeasuredWeight,
): number | null;
export function getValueByColumn(
  row: LivestockInfoFromFarm,
  column: LotListTableColumn.WeighingDate,
): string | null;
export function getValueByColumn(
  row: LivestockInfoFromFarm,
  column: LotListTableColumn.ProjectedWeight,
  locale: Locales,
  changedMeasuredWeight?: number | null,
): string | null;
export function getValueByColumn(
  row: LivestockInfoFromFarm,
  column: LotListTableColumn,
  locale?: Locales,
  changedMeasuredWeight?: number | null,
) {
  if (column === LotListTableColumn.Lot) {
    return row.animalCategory?.name ?? null;
  }

  if (column === LotListTableColumn.Quantity) {
    return row.quantity;
  }

  if (column === LotListTableColumn.Sanity) {
    return getSanityValue(row);
  }

  if (column === LotListTableColumn.Goal) {
    return row.goalId;
  }

  if (column === LotListTableColumn.GoalWeight) {
    return row.goalWeight?.weight ?? null;
  }

  if (column === LotListTableColumn.MeasuredWeight) {
    return row.measuredWeight?.weight ?? null;
  }

  if (column === LotListTableColumn.WeighingDate) {
    return row.measuredWeight?.weightDate ?? null;
  }

  if (column === LotListTableColumn.ProjectedWeight) {
    // Consider the measured weight as more up-to-date than the projected weight only if it was created after the projected weight.
    if (changedMeasuredWeight != null) {
      return getCurrentMonthWeightProjection(
        changedMeasuredWeight,
        changedMeasuredWeight,
        locale as Locales,
      );
    }

    if (row.projectedWeight?.createdAt && row.measuredWeight?.createdAt) {
      const showMeasureWeight =
        new Date(row.measuredWeight.createdAt) >
        new Date(row.projectedWeight.createdAt);

      if (showMeasureWeight) {
        return getCurrentMonthWeightProjection(
          row.measuredWeight?.weight,
          row.measuredWeight?.weight,
          locale as Locales,
        );
      }
    }

    const projectedWeightMessage = getCurrentMonthWeightProjection(
      row.projectedWeight?.weight,
      row.measuredWeight?.weight,
      locale as Locales,
    );

    return projectedWeightMessage;
  }

  return null;
}

const monthNumberToCurveKey: Record<number, keyof WeightGainCurve> = {
  0: "january",
  1: "february",
  2: "march",
  3: "april",
  4: "may",
  5: "june",
  6: "july",
  7: "august",
  8: "september",
  9: "october",
  10: "november",
  11: "december",
};

export function getDateForGoalWeight(
  enrichedAnimalCategories: EnrichedAnimalCategory[],
  row: LivestockInfoFromFarm,
  targetWeight: number | null | undefined,
): string | null {
  if (!targetWeight || targetWeight <= 0) {
    return null;
  }

  if (!row.measuredWeight?.weight || !row.measuredWeight?.weightDate) {
    return null;
  }

  const enrichedCategory = enrichedAnimalCategories.find(
    (cat) => cat.id === row.animalCategoryId,
  );

  const weightGainCurve = enrichedCategory?.weightGainCurve;
  if (!weightGainCurve) {
    return null;
  }

  const currentWeight = row.measuredWeight.weight;

  if (currentWeight >= targetWeight) {
    return DateUtils.parse(row.measuredWeight.weightDate).format(
      DATE_FORMATS["DD/MM/YYYY"],
    );
  }

  // Pre-calculate monthly weight gains for each month
  const monthlyGains = Object.entries(monthNumberToCurveKey).reduce(
    (acc, [monthNum, monthKey]) => {
      acc[parseInt(monthNum)] = weightGainCurve[monthKey] as number;
      return acc;
    },
    {} as Record<number, number>,
  );

  let projectedWeight = currentWeight;
  let currentDate = DateUtils.parse(row.measuredWeight.weightDate);
  let daysElapsed = 0;

  const MAX_DAYS = 365 * 2; // 2 years maximum

  while (daysElapsed < MAX_DAYS) {
    currentDate = currentDate.add(1, DATE_UNIT.DAY);
    daysElapsed++;

    const monthValue = currentDate.month();
    const monthlyGain = monthlyGains[monthValue] ?? 0;

    projectedWeight += monthlyGain;

    if (projectedWeight >= targetWeight) {
      return currentDate.format(DATE_FORMATS["DD/MM/YYYY"]);
    }
  }

  return null;
}

export function getCurrentMonthWeightProjection(
  projectedWeight: number | null | undefined,
  measuredWeight: number | null | undefined,
  locale: Locales,
): string | null {
  const now = DateUtils.now();
  const monthName = now.monthName(locale);

  if (projectedWeight != null && measuredWeight != null) {
    return `${monthName} - ${projectedWeight.toFixed(2)}`;
  }

  return null;
}

function isFilteredByRetreat(
  lot: LivestockInfoFromFarm,
  filter: LotsFilterFormState,
): boolean {
  if (filter.retreatId === RETREAT_FILTER_ALL_VALUE) {
    return true;
  }

  return lot.retreat?.id === filter.retreatId;
}

function isFilteredBySearchText(
  lot: LivestockInfoFromFarm,
  filter: LotsFilterFormState,
): boolean {
  if (filter.searchText.trim() === "") {
    return true;
  }

  const matchesByCategory = lot.animalCategory?.name
    .toLowerCase()
    .includes(filter.searchText.toLowerCase());

  const matchesByPasture = [lot.pasture?.name, lot.pasture?.shortName]
    .join(" ")
    .toLowerCase()
    .includes(filter.searchText.toLowerCase());

  const matchesByRetreat = [lot.retreat?.name, lot.retreat?.shortName]
    .join(" ")
    .toLowerCase()
    .includes(filter.searchText.toLowerCase());

  return matchesByCategory || matchesByPasture || matchesByRetreat;
}

function isFilteredByMeasuredWeight(
  lot: LivestockInfoFromFarm,
  filter: LotsFilterFormState,
): boolean {
  if (filter.measuredWeight === MeasuredWeightFilter.All) {
    return true;
  }

  if (filter.measuredWeight === MeasuredWeightFilter.Informed) {
    return lot.measuredWeight?.weight != null && lot.measuredWeight.weight > 0;
  }

  return !lot.measuredWeight?.weight || lot.measuredWeight.weight === 0;
}

function isFilteredByProjectedWeight(
  lot: LivestockInfoFromFarm,
  filter: LotsFilterFormState,
): boolean {
  if (filter.projectedWeight === ProjectedWeightFilter.All) {
    return true;
  }

  if (filter.projectedWeight === ProjectedWeightFilter.Available) {
    return (
      lot.projectedWeight?.weight != null && lot.projectedWeight.weight > 0
    );
  }

  return !lot.projectedWeight?.weight || lot.projectedWeight.weight === 0;
}

export function filterLotPredicate(
  lot: LivestockInfoFromFarm,
  filterForm: LotsFilterFormState,
): boolean {
  const matchesByRetreat = isFilteredByRetreat(lot, filterForm);
  const matchesBySearchText = isFilteredBySearchText(lot, filterForm);
  const matchesByMeasuredWeight = isFilteredByMeasuredWeight(lot, filterForm);
  const matchesByProjectedWeight = isFilteredByProjectedWeight(lot, filterForm);

  return (
    matchesByRetreat &&
    matchesBySearchText &&
    matchesByMeasuredWeight &&
    matchesByProjectedWeight
  );
}

export const monthNumberToProjectionKey: Record<number, keyof ProjectedWeight> =
  {
    0: "january",
    1: "february",
    2: "march",
    3: "april",
    4: "may",
    5: "june",
    6: "july",
    7: "august",
    8: "september",
    9: "october",
    10: "november",
    11: "december",
  };

export function parseProjectionToTableData(
  projection: ProjectedWeight | null,
  locale: Locales,
): Array<{ date: string; expectedWeight: number; month: number }> {
  if (!projection) {
    return [];
  }

  const currentDate = DateUtils.now();
  const currentMonth = currentDate.month();

  const semesterMonths = Array.from(
    { length: 6 },
    (_, i) => (currentMonth + i) % 12,
  );

  const monthKeys = semesterMonths.map((month) => ({
    key: monthNumberToProjectionKey[month],
    month,
  }));

  const tableData = monthKeys.map(({ key, month }, index) => {
    const monthNumber = semesterMonths[index];
    const year =
      currentMonth <= monthNumber ? currentDate.year() : currentDate.year() + 1;

    const baseDate = DateUtils.parse(new Date(year, monthNumber, 1));
    const lastDayOfMonth = baseDate.endOf(DATE_UNIT.MONTH).date();
    const monthName = baseDate.monthName(locale);
    const capitalizedMonthName =
      monthName.charAt(0).toUpperCase() + monthName.slice(1);

    const text = [lastDayOfMonth, capitalizedMonthName].join("/");

    return {
      date: text,
      expectedWeight: (projection[key] as number | null | undefined) ?? 0,
      month,
    };
  });

  return tableData;
}

export function getValueToUpdate<T>(
  value: T | null | undefined,
  originalValue: T | null,
): Exclude<T, undefined> | null {
  if (value !== undefined) {
    return value as Exclude<T, undefined> | null;
  }

  return originalValue as Exclude<T, undefined> | null;
}
